import type { PropType } from "vue";

import { computed, reactive, toRef } from "vue";

import { useI18nCommonFe } from "@solvari/translations";

import type { Size } from "@/lib/components/types";

import { size } from "@/lib/components/logic/atoms/input/props";
import { propsDefinition } from "@/lib/composables/componentComposable";

type UseLabelScoped = {
  disabled?: boolean;
  label?: string;
  required?: boolean;
  showRequiredType?: "none" | "optional" | "required";
  size?: Size;
};

type UseLabelProps = UseLabelScoped & {
  for?: string;
  labelId?: string;
  tag?: string;
};

const scoped = propsDefinition({
  disabled: { type: Boolean, default: false },
  label: { type: String, required: false },
  required: { type: Boolean, default: false },
  showRequiredType: {
    type: String as PropType<"none" | "optional" | "required">,
    default: "required",
  },
  size,
}) satisfies Record<keyof UseLabelScoped, unknown>;

const props = propsDefinition({
  ...scoped,
  for: { type: String, required: false },
  labelId: { type: String, required: false },
  tag: { type: String, default: "label" },
}) satisfies Record<keyof UseLabelProps, unknown>;

function use(props: UseLabelProps) {
  const { tr } = useI18nCommonFe();

  const suffix = computed(() => {
    if (props.showRequiredType === "optional" && !props.required) {
      return tr("common_fe.fields.optional");
    }

    if (props.showRequiredType === "required" && props.required) {
      return "*";
    }

    return null;
  });

  return {
    wrapper: {
      props: reactive({
        for: toRef(() => props.for),
        id: toRef(() => props.labelId),
      }),
    },
    size: toRef(() => props.size),
    tag: toRef(() => props.tag),
    label: toRef(() => props.label),
    suffix,
    disabled: toRef(() => props.disabled),
  };
}

export type { UseLabelProps, UseLabelScoped };
export { props, scoped, use };
